/**
 * Local scripted knowledge base for the "Ask my portfolio" demo assistant.
 *
 * There is NO LLM or network call. Each intent carries a set of keywords and a
 * scripted answer. The matcher lowercases the query, scores each intent by how
 * many of its keywords appear, and returns the highest-scoring answer. If no
 * intent scores, a friendly fallback is returned.
 *
 * Content scope is intentionally constrained to match the current site —
 * see the brief. Do not reintroduce removed facts (e.g. specific AI clients).
 */

export const SUGGESTED_QUESTIONS = [
  'What does Sébastien do?',
  'What has he built?',
  "What's his AI experience?",
  'Where is he based?',
  'How can I reach him?',
  'Main skills?',
];

export const GREETING =
  "Hi! I'm a little demo assistant for Sébastien's portfolio. Ask me about his work, his AI experience, his skills, where he's based, or how to get in touch.";

export const FALLBACK =
  "I'm a little demo assistant — ask me about Sébastien's experience, his AI work, his skills, where he's based, or how to get in touch.";

/**
 * @typedef {{ id: string, keywords: string[], answer: string }} Intent
 */

/** @type {Intent[]} */
export const INTENTS = [
  {
    id: 'who',
    keywords: [
      'who',
      'what does',
      'what do you do',
      'about',
      'summary',
      'overview',
      'role',
      'profession',
      'job',
      'engineer',
      'developer',
      'introduce',
      'sebastien',
      'sébastien',
      'tell me about him',
      'bio',
    ],
    answer:
      "Sébastien is a full-stack engineer based in Paris who ships React, Next.js and React-Native products end-to-end as a freelancer — from first mockup to production.",
  },
  {
    id: 'current-role',
    keywords: [
      'current',
      'now',
      'today',
      'freelance',
      'freelancing',
      'job right now',
      'working',
      'work now',
      'employer',
      'client',
      'clients',
    ],
    answer:
      'Right now he works as a freelance full-stack engineer in Paris — React, React Native and Expo, owning the frontend end-to-end. His flagship engagement is PassCE, a corporate benefits platform reaching 10,000+ daily users on web and mobile.',
  },
  {
    id: 'experience',
    keywords: [
      'experience',
      'background',
      'history',
      'past',
      'previous',
      'career',
      'passce',
      'pass ce',
      'crédit agricole',
      'credit agricole',
      'benefits',
      'worked before',
      'companies',
    ],
    answer:
      'He works as a freelance full-stack engineer (React, React Native, Expo) — his flagship there is PassCE, a corporate benefits platform reaching 10,000+ daily users on web and mobile. Before that he was a Software Engineer at Crédit Agricole Technologies & Services, building internal banking apps in Java.',
  },
  {
    id: 'projects',
    keywords: [
      'project',
      'projects',
      'built',
      'made',
      'portfolio',
      'spotify',
      'spotify profiler',
      'reinforcement',
      'work samples',
      'apps',
      'side project',
    ],
    answer:
      'A few of his projects: PassCE (a corporate benefits platform on web and mobile), Spotify Profiler (a React + Node lyrics & listening-stats explorer), and an "AI: Reinforcement Learning" class/research project.',
  },
  {
    id: 'ai',
    keywords: [
      'ai',
      'a.i.',
      'artificial intelligence',
      'machine learning',
      'ml',
      'llm',
      'llms',
      'rag',
      'retrieval',
      'reinforcement',
      'reinforcement learning',
      'gpt',
      'model',
      'models',
      'image processing',
      'neural',
    ],
    answer:
      'On AI: he has built AI-powered products and worked with LLMs and Retrieval-Augmented Generation (RAG), done a reinforcement-learning project, and studied image processing & artificial intelligence. He\'s comfortable integrating AI/LLMs into full-stack products. (This very chat is a small scripted demo, not a real model.)',
  },
  {
    id: 'skills',
    keywords: [
      'skill',
      'skills',
      'stack',
      'tech',
      'technologies',
      'tools',
      'languages he codes',
      'frameworks',
      'react',
      'next',
      'typescript',
      'node',
      'python',
      'graphql',
      'expertise',
      'good at',
    ],
    answer:
      'His stack: Frontend — React, Next.js, React Native, Tailwind, TypeScript. Backend — Node, Nest.js, Java, Python, GraphQL. Mobile — Expo, Swift, Kotlin. Cloud/tools — GCP, AWS, Firebase, Git, Linux. Plus AI/LLM work.',
  },
  {
    id: 'languages',
    keywords: [
      'language',
      'languages',
      'speak',
      'speaks',
      'spoken',
      'french',
      'english',
      'spanish',
      'multilingual',
      'fluent',
    ],
    answer:
      'Languages spoken: French (native) and English (fluent), plus Spanish and the basics of a few others.',
  },
  {
    id: 'education',
    keywords: [
      'education',
      'study',
      'studied',
      'school',
      'university',
      'degree',
      'efrei',
      'msc',
      'master',
      'barcelona',
      'concordia',
      'montreal',
      'catalunya',
      'upc',
      'exchange',
      'graduate',
    ],
    answer:
      'Education: EFREI Paris (Software Engineering, MSc), an exchange at Universitat Politècnica de Catalunya in Barcelona (Image Processing & AI), and an exchange at Concordia University in Montreal (Software & Data Engineering).',
  },
  {
    id: 'location',
    keywords: [
      'where',
      'based',
      'location',
      'live',
      'lives',
      'living',
      'city',
      'cities',
      'paris',
      'barcelona',
      'montreal',
      'travel',
      'located',
      'from',
    ],
    answer:
      "He's based in Paris, and has lived and studied in Montreal and Barcelona. He travels widely.",
  },
  {
    id: 'contact',
    keywords: [
      'contact',
      'reach',
      'reach out',
      'get in touch',
      'touch',
      'hire',
      'hiring',
      'available',
      'availability',
      'message',
      'connect',
      'linkedin',
      'github',
      'email',
      'social',
      'dm',
    ],
    answer:
      "He's open to interesting projects and conversations. The best way to reach him is via LinkedIn (linkedin.com/in/sebastien-friedberg) or GitHub (github.com/SebastienGt).",
  },
  {
    id: 'music',
    keywords: [
      'music',
      'piano',
      'hobby',
      'hobbies',
      'fun',
      'passion',
      'interest',
      'interests',
      'like',
      'likes',
      'enjoy',
    ],
    answer:
      'He loves blending music and technology — he plays piano, and music-tech is a real passion of his.',
  },
];

/**
 * Score every intent against the query and return the best matching answer.
 * Pure function, no side effects, safe in any environment (SSR/prerender).
 *
 * @param {string} query
 * @returns {{ answer: string, intentId: string | null }}
 */
export function matchAnswer(query) {
  const text = (query || '').toLowerCase().trim();

  if (!text) {
    return { answer: FALLBACK, intentId: null };
  }

  let best = { score: 0, intent: null };

  for (const intent of INTENTS) {
    let score = 0;

    for (const keyword of intent.keywords) {
      if (text.includes(keyword)) {
        // Longer, multi-word keywords are stronger signals.
        score += keyword.includes(' ') ? 2 : 1;
      }
    }

    if (score > best.score) {
      best = { score, intent };
    }
  }

  if (!best.intent) {
    return { answer: FALLBACK, intentId: null };
  }

  return { answer: best.intent.answer, intentId: best.intent.id };
}
